import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";

/**
 * Плашка результата скана — ПОВЕРХ списка, а не вместо него.
 *
 * Почему не полноэкранный герой: главное на экране сбора — список к сборке
 * (сколько чего осталось), и он не должен исчезать на каждый скан. Плашка
 * ложится на нижнюю часть списка, верх (куда уезжает только что
 * отсканированная позиция) остаётся видимым.
 *
 * Почему не автозакрытие: незамеченный «стикер не напечатан» = штука, которую
 * в ПВЗ не примут. Плашка живёт до следующего скана или до закрытия рукой;
 * ошибка/предупреждение при этом крупная и цветная — её нельзя пролистать
 * взглядом.
 *
 * Плашка — обычный элемент разметки, а НЕ шторка: перехват скана она не гасит,
 * сбор продолжается с открытой плашкой.
 */
export type BannerTone = "ok" | "warn" | "error" | "info";

const TONE: Record<BannerTone, { box: string; title: string; icon: string }> = {
  ok: { box: "border-emerald-300 bg-emerald-50", title: "text-emerald-900", icon: "text-emerald-600" },
  warn: { box: "border-amber-400 bg-amber-100", title: "text-amber-900", icon: "text-amber-600" },
  error: { box: "border-red-400 bg-red-100", title: "text-red-900", icon: "text-red-600" },
  info: { box: "border-sky-200 bg-sky-50", title: "text-sky-900", icon: "text-sky-600" },
};

export function ScanBanner({
  tone,
  title,
  onClose,
  children,
  actions,
}: {
  tone: BannerTone;
  title: string;
  /** null — закрывать нечего (плашка «ищем в 1С» уходит сама, по ответу). */
  onClose: (() => void) | null;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}): React.ReactElement {
  const t = TONE[tone];
  // Крупно — всё, что кладовщик обязан заметить: ошибка скана и незакрытый
  // хвост (партия/стикер/срок). Удачный скан подтверждается компактно.
  const big = tone === "warn" || tone === "error";
  const Icon = tone === "info" ? Loader2 : tone === "ok" ? Check : AlertTriangle;

  return (
    <div
      className={cn(
        "max-h-[70vh] overflow-y-auto rounded-xl border-2 shadow-lg",
        t.box,
        big ? "p-3.5" : "p-2.5",
      )}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={cn(
            "shrink-0",
            t.icon,
            big ? "size-8" : "size-6",
            tone === "info" && "animate-spin",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className={cn("font-bold leading-tight", t.title, big ? "text-[18px]" : "text-[15px]")}>
            {title}
          </div>
          {children}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="-m-1 shrink-0 p-1 text-slate-500"
          >
            <X className="size-5" />
          </button>
        )}
      </div>
      {actions && <div className="mt-2 flex gap-2">{actions}</div>}
    </div>
  );
}

/** Обёртка позиционирования: плашка прижата к низу главной области.
 *  `pointer-events-none` на подложке — чтобы поля вокруг плашки не перехватывали
 *  скролл списка под ней. */
export function ScanBannerSlot({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-2">
      <div className="pointer-events-auto">{children}</div>
    </div>
  );
}

/** Код, пойманный при выключенном перехвате (`useScanCapture.onBlocked`). */
export interface BlockedScan {
  raw: string;
  /** Что мешает и что сделать — словами для кладовщика. */
  reason: string;
}

/** «Скан не принят» — код дошёл до портала, но засчитан НЕ был.
 *
 * ⚠️ Молчать нельзя: человек видит только экран, и тишину после выстрела
 * засчитает за «принято» (так было с кольцом на приёмке возвратов, 28.10.2026).
 * Плашка — обычный элемент потока, живёт до следующего принятого скана или до
 * закрытия рукой. */
export function BlockedScanBanner({
  blocked,
  onClose,
}: {
  blocked: BlockedScan;
  onClose: () => void;
}): React.ReactElement {
  return (
    <ScanBanner tone="error" title="Скан не принят" onClose={onClose}>
      <p className="mt-1 text-[14px] text-red-900">{blocked.reason}</p>
      <p className="mt-1 break-all font-mono text-[11px] text-slate-600">{blocked.raw}</p>
    </ScanBanner>
  );
}
